import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { BellIcon, CalendarDaysIcon, MapIcon, MaximizeIcon, MinimizeIcon, TruckIcon } from 'lucide-react';
import { collections, liveCollection, materials } from '../../data/collectes';
import { useLiveTruck, useTruckViewer } from '../../hooks/useLiveTruck';
import { TruckBoostButton } from './TruckBoostButton';
import { token } from '../../utils/theme';
const TRUCK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';
const HOME_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 10v10h14V10"/></svg>';
const truckIcon = L.divIcon({
  className: 'collecte-truck',
  html: `<span class="grid h-9 w-9 place-items-center rounded-xl border-2 border-white bg-ville-blue shadow-lg shadow-ville-navy/30">${TRUCK_SVG}</span>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18]
});
const homeIcon = L.divIcon({
  className: 'collecte-home',
  html: `<span class="grid h-8 w-8 place-items-center rounded-full border-[3px] border-white bg-ville-navy shadow-md shadow-ville-navy/30">${HOME_SVG}</span>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});

/** Libellé de la prochaine collecte, repris de la liste pour ne jamais la contredire. */
const nextCollection = collections[1];
const nextCollectionLabel = `${nextCollection.weekday} ${nextCollection.date}`;
interface CollectionTrackerProps {
  remindersOpen?: boolean;
  onToggleReminders?: () => void;
}
export function CollectionTracker({
  remindersOpen = false,
  onToggleReminders
}: CollectionTrackerProps) {
  const {
    home,
    truck,
    material
  } = liveCollection;
  useTruckViewer();
  const {
    route,
    done,
    remaining,
    truckPosition,
    distance,
    minutes,
    arrived,
    arrivingSoon
  } = useLiveTruck();
  const [expanded, setExpanded] = useState(false);
  const [collectionToday, setCollectionToday] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: home,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [home]);
  useEffect(() => {
    if (!mapRef.current) return;
    if (!collectionToday) {
      mapRef.current.setView(home, 15);
      return;
    }
    if (route.length < 2) return;
    mapRef.current.fitBounds(L.latLngBounds(route), {
      padding: [34, 34]
    });
  }, [route, collectionToday, home]);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!collectionToday) {
      L.marker(home, {
        icon: homeIcon,
        title: 'Votre adresse',
        alt: 'Votre adresse'
      }).addTo(layer);
      return;
    }
    L.polyline(done, {
      color: '#94a3b8',
      weight: 4,
      opacity: 0.65
    }).addTo(layer);
    L.polyline(remaining, {
      color: token('--ville-blue', '#2f5bc9'),
      weight: 5,
      dashArray: '1 9',
      lineCap: 'round'
    }).addTo(layer);
    L.marker(home, {
      icon: homeIcon,
      title: 'Votre adresse',
      alt: 'Votre adresse'
    }).addTo(layer);
    L.marker(truckPosition, {
      icon: truckIcon,
      title: truck,
      alt: `Position du ${truck}`
    }).bindTooltip(truck, {
      direction: 'top',
      offset: [0, -18]
    }).addTo(layer);
  }, [done, remaining, truckPosition, home, truck, collectionToday]);

  /** Redimensionne et recadre la carte quand la fiche passe en plein écran et revient. */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const map = mapRef.current;
      if (!map) return;
      map.invalidateSize();
      if (!collectionToday) {
        map.setView(home, 15);
        return;
      }
      if (route.length > 1) map.fitBounds(L.latLngBounds(route), {
        padding: [34, 34]
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [expanded, route, collectionToday, home]);
  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded]);
  const card = <section aria-labelledby="suivi-collecte-titre" className={expanded ? 'relative isolate m-auto flex h-full w-full max-w-[1000px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-ville-navy/30' : 'relative isolate z-0 flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white'} data-id="element-976" data-mp="17ovete71fzsl">
      <div className="flex items-start justify-between gap-3 px-5 pb-4 pt-5" data-id="element-977" data-mp="1qnzjybvgl9ez">
        <div className="min-w-0" data-id="element-978" data-mp="1qlwjsz0ldzwj">
          <h2 id="suivi-collecte-titre" className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-979" data-mp="1lpek4dk4crr2">
            <TruckIcon className="h-5 w-5 text-ville-blue" aria-hidden="true" data-id="element-980" data-mp="2j6d79hj65czt" />
            {collectionToday ? 'Collecte en cours' : 'Suivi du camion en direct'}
          </h2>
          <p className="mt-0.5 truncate text-sm text-slate-500" data-id="element-981" data-mp="cgjokfbq2ca3">
            {collectionToday ? materials[material].label : `Prochaine collecte · ${nextCollectionLabel}`}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${!collectionToday ? 'bg-slate-100 text-slate-600' : arrivingSoon ? 'bg-ville-mist text-ville-deep' : 'bg-slate-100 text-slate-600'}`} data-id="element-982" data-mp="2pp157mqp6n2">
          {!collectionToday ? 'Hors service' : arrived ? 'Sur place' : arrivingSoon ? 'Arrive bientôt' : 'En route'}
        </span>
      </div>

      <div className={expanded ? 'relative min-h-0 flex-1' : 'relative'} data-id="element-983" data-mp="1e5dmxvo9bzma">
        <div ref={containerRef} role="application" aria-label={`Trajet du ${truck} vers votre adresse`} className={expanded ? 'h-full w-full bg-ville-mist' : 'h-[260px] w-full bg-ville-mist sm:h-[300px]'} data-id="element-984" data-mp="1e3aocn91gy5y" />
        <button type="button" onClick={() => setExpanded(current => !current)} aria-label={expanded ? 'Réduire la carte' : 'Agrandir la carte'} className="absolute left-3 top-3 z-[500] inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3.5 py-2 text-xs font-bold text-ville-navy shadow-md shadow-ville-navy/10 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-985" data-mp="1oadbdf5hysrg">
          {expanded ? <MinimizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-986" data-mp="2gxg9w7a6b3hp" /> : <MaximizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-987" data-mp="qla9mj566frp" />}
          {expanded ? 'Réduire' : 'Agrandir'}
        </button>

        {collectionToday && <TruckBoostButton className="absolute bottom-3 left-3 z-[500]" data-id="element-988" data-mp="mlrp3oh9g6pe" />}

        {!collectionToday && <div className="absolute inset-0 z-[450] grid place-items-center bg-ville-navy/45 p-5 backdrop-blur-[2px]" data-id="element-989" data-mp="1b2sn22fcl9kq">
            <div className="max-w-sm rounded-2xl bg-white/95 px-5 py-4 text-center shadow-lg shadow-ville-navy/20" data-id="element-990" data-mp="1kuokdb9m0vnw">
              <p className="font-extrabold text-ville-navy" data-id="element-991" data-mp="1owtcg5ketk8f">Aucune collecte aujourd’hui</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-992" data-mp="s6al6s6sbtar">
                Votre prochaine collecte est le {nextCollectionLabel.toLowerCase()}. Le camion apparaîtra ici, en
                direct sur la carte, le jour de la collecte.
              </p>
            </div>
          </div>}
      </div>

      {collectionToday ? <div className="grid grid-cols-2 divide-x divide-slate-100 border-t border-slate-100" data-id="element-993" data-mp="167dbjh4tbprh">
          <div className="px-5 py-4 text-center" data-id="element-994" data-mp="2cl2vx0u5hvwe">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-995" data-mp="2hkp6t3mib021">Arrivée estimée</p>
            <p className="mt-1 text-2xl font-extrabold tabular-nums text-ville-blue" data-id="element-996" data-mp="2romdp5sbd33y">
              {arrived ? 'Sur place' : `${minutes} min`}
            </p>
          </div>
          <div className="px-5 py-4 text-center" data-id="element-997" data-mp="2ms5e9ffnz2bk">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-998" data-mp="2hy16wv2hks9y">Distance actuelle</p>
            <p className="mt-1 text-2xl font-extrabold tabular-nums text-ville-navy" data-id="element-999" data-mp="2dwppvdxohfdd">
              {distance < 0.1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1).replace('.', ',')} km`}
            </p>
          </div>
        </div> : <div className="border-t border-slate-100 px-5 py-4 text-center" data-id="element-1000" data-mp="1z08bsey3tmn4">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-1001" data-mp="1z2b455mqbrv0">Prochaine collecte</p>
          <p className="mt-1 text-2xl font-extrabold text-ville-navy" data-id="element-1002" data-mp="1v0zlxl66q8wu">{nextCollectionLabel}</p>
        </div>}

      {!expanded && <div className="border-t border-slate-100 px-5 py-3 text-center" data-id="element-1003" data-mp="3pcecg470scdn">
          <button type="button" onClick={() => setCollectionToday(current => !current)} aria-pressed={!collectionToday} className="text-xs font-bold text-slate-500 underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1004" data-mp="ysniz7dgtthj">
            {collectionToday ? 'Aperçu d’une journée sans collecte' : 'Revenir au suivi en direct'}
          </button>
        </div>}

      {!expanded && <nav aria-label="Raccourcis collectes" className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100" data-id="element-1005" data-mp="b4nru6c8s12f">
          {[{
        to: '/mes-collectes/calendrier',
        label: 'Calendrier',
        Icon: CalendarDaysIcon
      }, {
        to: '/plan',
        label: 'Carte',
        Icon: MapIcon
      }].map(({
        to,
        label,
        Icon
      }) => <Link key={label} to={to} className="flex flex-col items-center gap-1.5 px-2 py-4 text-xs font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-sand hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1006" data-mp="3oir00lt39v0c">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1007" data-mp="1ux5nvdszcmti">
                <Icon className="h-5 w-5" data-id="element-1008" data-mp="2mqxyo2qw1g58" />
              </span>
              {label}
            </Link>)}
          <button type="button" onClick={onToggleReminders} aria-expanded={remindersOpen} className={`flex flex-col items-center gap-1.5 px-2 py-4 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${remindersOpen ? 'bg-ville-sand text-ville-deep' : 'text-ville-navy hover:bg-ville-sand hover:text-ville-deep'}`} data-id="element-1009" data-mp="2ropzxndfaem3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1010" data-mp="38ybt36lx62g8">
              <BellIcon className="h-5 w-5" data-id="element-1011" data-mp="2z88r4gwtle5r" />
            </span>
            Rappels
          </button>
        </nav>}
    </section>;
  if (!expanded) return card;
  return <div role="dialog" aria-modal="true" aria-label="Carte de collecte agrandie" className="fixed inset-0 z-[60] flex bg-ville-navy/60 p-4 backdrop-blur-sm sm:p-8" onClick={event => {
    if (event.target === event.currentTarget) setExpanded(false);
  }} data-id="element-1012" data-mp="2q82ib8c2h2cx">
      {card}
    </div>;
}